import get from 'lodash/get';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
  getReportById,
  getCampaigns,
  getWorkflows,
  getReportData,
  getReportSummaryData,
  saveReport,
  exportReportDataAsCsv,
} from 'api/ReportingAPI';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { getErrorResponse } from 'utils/xhr';
import { guessCurrentTimeZone } from 'utils/timeZone';
import { addToRootSaga } from 'store';
import * as actions from './actions';
import * as actionTypes from './actionTypes';
import { selectReportById, selectHighLevelMetrics, dataSourceModal } from './selectors';
import { ActiveMenuItemEnum } from './types';

export function* fetchSingleReport({ id }) {
  try {
    const { data: report } = yield call(getReportById, id);
    yield put(actions.fetchReportResponse(report));
    yield put(
      actions.updateChartType(
        report.id,
        ActiveMenuItemEnum.componentTable,
        get(report, 'settings.entityView.chartType', 'bar'),
      ),
    );
    yield put(actions.fetchReportDataRequest(id));
  } catch (error) {
    yield put(actions.fetchReportError(error));
  }
}

export function* saveReportRequest({ report }) {
  try {
    yield call(saveReport, report);
    yield put(actions.saveReportResponse(report));
  } catch (error) {
    yield put(actions.saveReportError(error, report));
  }
}

export function* fetchReportsTableData({ reportsTableRequest }) {
  try {
    const entityApiCall =
      reportsTableRequest.reportingType === 'Workflow' ? getWorkflows : getCampaigns;
    const response = yield call(entityApiCall, reportsTableRequest.filter);
    yield put(
      actions.fetchReportingTypeTableResponse({
        response,
        selectedTypeIds: reportsTableRequest.selectedTypeIds,
      }),
    );
  } catch (error) {
    yield put(actions.fetchReportingTypeTableError(error));
  }
}

// TODO should probably consolidate this with the generator above
export function* fetchUpdatedReportTable() {
  // this happens when the modal is open and the user pages/sorts/filters the table
  try {
    const data = yield select(dataSourceModal);
    const entityApiCall = data.componentType === 'Workflow' ? getWorkflows : getCampaigns;
    const response = yield call(entityApiCall, data.tableConfig);
    let campaignIdResponse = null; // logic to address issue seen here: https://iterable.atlassian.net/browse/ANA-127
    if (data.componentType !== 'Workflow' && parseInt(data.tableConfig.filter, 10)) {
      const { filter, ...restOfConfig } = data.tableConfig;
      campaignIdResponse = yield call(getCampaigns, {
        ...restOfConfig,
        query: '',
        recurringCampaignId: filter,
      });
    }
    yield put(
      actions.fetchReportingTypeTableResponse(
        {
          response,
          selectedTypeIds: data.selectedTypeIds,
          selectedTypeIdsInModal: data.selectedTypeIdsInModal,
          campaignIdResponse,
        },
        true,
      ),
    );
  } catch (error) {
    yield put(actions.fetchReportingTypeTableError(error));
  }
}

export function* updateMetricsList({ reportId, metricsList }) {
  try {
    const highLevelMetrics = yield select(selectHighLevelMetrics, metricsList);
    const selectedMetrics = metricsList.map((m) =>
      highLevelMetrics.find((hlm) => hlm.enumName === m),
    );
    yield put(actions.metricsListUpdated(reportId, selectedMetrics));
    yield put(actions.fetchReportDataRequest(reportId));
  } catch (error) {
    yield put(actions.updateMetricsListError(error));
  }
}

export function* fetchReportData({ reportId }) {
  try {
    const report = yield select(selectReportById, { id: reportId });
    if (
      report.componentType &&
      report.rangeStart &&
      report.rangeEnd &&
      report.componentIds.length > 0 &&
      report.selectedMetrics.length > 0
    ) {
      const [dateTimeResponse, summaryResponse] = yield all([
        call(getReportData, report),
        call(getReportSummaryData, report),
      ]);
      yield put(
        actions.fetchReportDataResponse(reportId, dateTimeResponse.data, summaryResponse.data),
      );
    } else {
      yield put(
        actions.fetchReportDataResponse(
          reportId,
          { dateTimeViewMetrics: [] },
          { campaignViewMetrics: [] },
        ),
      );
    }
  } catch (error) {
    yield put(actions.fetchReportDataError(error));
  }
}

export function* exportReportDataAsCsvSaga({ reportId, callback }) {
  try {
    yield put(
      addPositiveToast('Report download started', {
        key: 'reportCsvExportStart',
        noDelay: true,
      }),
    );
    const timeZone = guessCurrentTimeZone();
    const { data } = yield call(exportReportDataAsCsv, reportId, timeZone);
    yield put(actions.exportReportDataAsCsvResponse(data));
    callback(data);
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(actions.exportReportDataAsCsvError(err));
    yield put(
      addNegativeToast(`Report download failed: ${err}`, {
        key: 'reportCsvExportError',
        noDelay: true,
      }),
    );
  }
}

export function* saveAndExportReportDataAsCsvSaga({ report, callback }) {
  yield saveReportRequest({ report });
  yield exportReportDataAsCsvSaga({ reportId: report.id, callback });
}

addToRootSaga([
  takeLatest(actionTypes.FETCH_REPORT_BY_ID_REQUEST, fetchSingleReport),
  takeLatest(actionTypes.FETCH_REPORT_MODAL_TABLE_DATA_REQUEST, fetchReportsTableData),
  takeLatest(actionTypes.UPDATE_REPORT_MODAL_TABLE_DATA, fetchUpdatedReportTable),
  takeLatest(actionTypes.REPORT_PARAMS_UPDATED, fetchReportData),
  takeLatest(actionTypes.UPDATE_METRICS_LIST, updateMetricsList),
  takeLatest(actionTypes.FETCH_REPORT_DATA_REQUEST, fetchReportData),
  takeLatest(actionTypes.UPDATE_REPORTING_TYPE_IDS, fetchReportData),
  takeLatest(actionTypes.SAVE_REPORT_REQUEST, saveReportRequest),
  takeLatest(actionTypes.EXPORT_REPORT_DATA_AS_CSV_REQUEST, exportReportDataAsCsvSaga),
  takeLatest(
    actionTypes.SAVE_AND_EXPORT_REPORT_DATA_AS_CSV_REQUEST,
    saveAndExportReportDataAsCsvSaga,
  ),
]);
